import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, CalendarClock, Search, MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ToolPromos, CatalogGroups, HowItWorks, Advantages, Testimonials, FinalCta } from "@/components/landing/LandingSections";

const HERO_IMG = "https://images.unsplash.com/photo-1752321532656-43e714c3deba?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080";

export default function Landing() {
  const [meta, setMeta] = useState({ categories: [], category_groups: [], zones: [] });
  const [q, setQ] = useState("");
  const [zone, setZone] = useState("");
  const [leadType, setLeadType] = useState("buyer");
  const [lead, setLead] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/meta").then((r) => {
      setMeta(r.data);
      if (r.data.zones.length) setZone(r.data.zones[0].id);
    }).catch(() => {});
  }, []);

  const search = (e) => {
    e.preventDefault();
    navigate(`/buyer?q=${encodeURIComponent(q)}`);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", { ...lead, type: leadType });
      toast.success(data.message);
      setLead({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-white to-sky-100/40" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-sky-200/40 rounded-full blur-[100px] translate-y-1/3" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-primary/8 px-4 py-2 text-sm font-medium text-primary">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" /></span>
              Bengaluru · delivering at site speed
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold tracking-tight mt-5 leading-[1.08] text-slate-900">
              Interior materials at <span className="text-primary">site speed.</span>
            </h1>
            <p className="mt-3 font-display text-lg sm:text-xl font-bold text-primary" data-testid="hero-tagline">Building Connections. Delivering Value.</p>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              Source plywood, laminates, veneers, hardware, stones, paints and 18 categories of finishes from verified nearby vendors.
              Exact ETA before you pay — 90-minute express where feasible, guaranteed scheduled freight everywhere else.
            </p>
            <form onSubmit={search} className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="hero-search-form">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Try &quot;18mm BWP plywood&quot; or &quot;Fevicol&quot;"
                  className="input-b2b !pl-10 !py-3"
                />
              </div>
              <select data-testid="hero-zone-select" value={zone} onChange={(e) => setZone(e.target.value)} className="input-b2b sm:!w-auto !py-3">
                {meta.zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
              </select>
              <button type="submit" data-testid="hero-search-btn" className="btn-brand !px-6 !py-3 whitespace-nowrap">
                Search materials
              </button>
            </form>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md border-t border-slate-200 pt-6">
              {[
                ["90 min", "conditional express SLA"],
                ["18", "categories · 50+ brands"],
                ["GST", "invoices on every order"],
              ].map(([a, b]) => (
                <div key={b}>
                  <p className="font-display text-2xl font-extrabold text-primary">{a}</p>
                  <p className="text-xs text-slate-500 mt-1">{b}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="hidden lg:block relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/10">
            <img src={HERO_IMG} alt="Interior material samples: tiles, wood and textures" className="h-[460px] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 rounded-xl bg-white/95 backdrop-blur-md p-4 flex items-center justify-between gap-3 border border-slate-200 shadow-lg" data-testid="hero-live-pill">
              <div><p className="text-sm font-bold text-slate-900">Express delivery active</p><p className="text-xs text-slate-500">Order now · dispatch within 30 min · at site in 90</p></div>
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/15"><span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" /><span className="relative inline-flex h-3 w-3 rounded-full bg-green-500" /></span></span>
            </div>
          </div>
        </div>
      </section>

      <ToolPromos />

      {/* EXPRESS VS SCHEDULED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="overline-label">How fulfilment works</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-3">Two delivery modes. Zero overpromising.</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <div className="card-surface p-8 border-l-4 !border-l-amber-500" data-testid="express-mode-card">
            <span className="badge-express"><Zap className="h-3 w-3" /> Express · 90 min SLA</span>
            <h3 className="font-display text-xl font-semibold mt-4 text-slate-900">When the site can't wait</h3>
            <ul className="mt-4 space-y-2 text-sm text-slate-600">
              <li>Only for express-eligible SKUs in approved zones</li>
              <li>Vendor accepts within 10 minutes or the order escalates</li>
              <li>Dispatch deadline enforced — breach triggers intervention</li>
              <li>Exact ETA shown before payment. Always.</li>
            </ul>
          </div>
          <div className="card-surface p-8 border-l-4 !border-l-emerald-500" data-testid="scheduled-mode-card">
            <span className="badge-scheduled"><CalendarClock className="h-3 w-3" /> Scheduled Freight</span>
            <h3 className="font-display text-xl font-semibold mt-4 text-slate-900">For everything else</h3>
            <ul className="mt-4 space-y-2 text-sm text-slate-600">
              <li>Bulky and non-bike-friendly items are scheduled-only by rule</li>
              <li>Next-day slots with committed delivery windows</li>
              <li>Automatic downgrade from express — never a fake promise</li>
              <li>Approved substitutes offered when an exact SKU is unavailable</li>
            </ul>
          </div>
        </div>
      </section>

      <CatalogGroups categories={meta.categories} groups={meta.category_groups || []} />
      <HowItWorks />

      {/* ZONES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <p className="overline-label">Serviceable area</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-3">Bengaluru launch zones</h2>
        <div className="mt-6 flex flex-wrap gap-3" data-testid="zone-chips">
          {meta.zones.map((z) => (
            <span key={z.id} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${z.express_supported ? "border-amber-500/40 bg-amber-500/10 text-amber-800" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-800"}`}>
              <MapPin className="h-3.5 w-3.5" /> {z.name} · {z.express_supported ? "Express" : "Scheduled only"}
            </span>
          ))}
        </div>
        <p className="mt-4 text-sm text-slate-500">Pilot coverage only. Express SLA applies where the zone is express-enabled and every SKU in the order is express-eligible.</p>
      </section>

      <Advantages />
      <Testimonials />

      {/* ONBOARDING */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" id="onboarding">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="overline-label">Get onboard</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-3">
              Built for interior companies & local vendors
            </h2>
            <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">
              We launch narrow on purpose: interior firms with repeat urgent needs, and nearby
              vendors/distributors with fast-moving inventory. Leave your details and our onboarding
              team will reach out within 24 hours.
            </p>
            <div className="mt-6 flex items-center gap-3 text-sm text-slate-600">
              <Building2 className="h-4 w-4 text-orange-700" /> procurement@materialexpress.in · +91 90000 00000
            </div>
          </div>
          <div className="card-surface p-6 sm:p-8" data-testid="lead-capture-card">
            <div className="flex gap-2 mb-6">
              {["buyer", "vendor"].map((t) => (
                <button
                  key={t}
                  data-testid={`lead-type-${t}`}
                  onClick={() => setLeadType(t)}
                  className={`flex-1 rounded-lg border px-4 py-2.5 text-sm font-semibold capitalize transition-colors ${leadType === t ? "border-orange-700 bg-orange-700 text-white" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                >
                  I'm a {t}
                </button>
              ))}
            </div>
            <form onSubmit={submitLead} className="space-y-4">
              <input data-testid="lead-name-input" required placeholder="Full name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="input-b2b" />
              <input data-testid="lead-email-input" required type="email" placeholder="Work email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="input-b2b" />
              <input data-testid="lead-phone-input" placeholder="Phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="input-b2b" />
              <input data-testid="lead-company-input" placeholder={leadType === "buyer" ? "Company (e.g. interior firm)" : "Shop / distribution name"} value={lead.company} onChange={(e) => setLead({ ...lead, company: e.target.value })} className="input-b2b" />
              <textarea data-testid="lead-message-input" rows={3} placeholder="What do you buy or sell, and in which zone?" value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} className="input-b2b" />
              <button type="submit" disabled={submitting} data-testid="lead-submit-btn" className="btn-brand w-full !py-3">
                {submitting ? "Submitting…" : `Request ${leadType} onboarding`}
              </button>
            </form>
          </div>
        </div>
      </section>

      <FinalCta />
    </div>
  );
}
